/**
 * イベント／イベントターゲットの realm 安全な構造判定。
 *
 * PIP（documentPictureInPicture）では keyboardTarget（container）が別ウインドウ
 * の document へ移動するため、そこで発火するイベントとその target は PIP 側
 * realm のコンストラクタから生成される。`instanceof KeyboardEvent` /
 * `instanceof HTMLElement` はメイン realm のコンストラクタとの比較になり false
 * を返すため、キー入力が黙って捨てられる（PIP で矢印キーのカメラ回転が効かな
 * かった原因）。instanceof ではなくプロパティの存在で構造的に判定する
 * （animationWindow.ts の isElementTarget と同じ方針）。
 *
 * 型述語は KeyboardEvent / HTMLElement 全体ではなく「実際に検証した構造」だけを
 * 約束する（検証していないプロパティへのアクセスを型で許さない）。
 */

export type KeyboardEventLike = Event &
  Pick<KeyboardEvent, 'code' | 'key' | 'repeat'>

export const isKeyboardEventLike = (event: Event): event is KeyboardEventLike =>
  'code' in event &&
  typeof event.code === 'string' &&
  'key' in event &&
  typeof event.key === 'string' &&
  'repeat' in event &&
  typeof event.repeat === 'boolean'

type HtmlElementLike = EventTarget &
  Pick<HTMLElement, 'isContentEditable' | 'tagName'>

const isHtmlElementLike = (
  target: EventTarget | null,
): target is HtmlElementLike =>
  target !== null &&
  'isContentEditable' in target &&
  typeof target.isContentEditable === 'boolean' &&
  'tagName' in target &&
  typeof target.tagName === 'string'

/** input/textarea/select/contentEditable へのキー入力はゲーム操作で横取りしない。 */
export const isTextEntryTarget = (target: EventTarget | null): boolean => {
  if (!isHtmlElementLike(target)) {
    return false
  }
  if (target.isContentEditable) {
    return true
  }
  const tag = target.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT'
}
